import React from 'react';
import { Link } from 'react-router-dom';
import styles from './BusinessPlan.module.css';

export default function BusinessPlan() {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>MyPeopleDatabase - Business Plan</h1>

        <div className={styles.plan}>
          <p className={styles.summary}>
            This page lays out a standard business plan approach for how MyPeopleDatabase becomes a
            sustainable product: who it serves, what it sells, how it reaches customers, and how it
            executes reliably over time.
          </p>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1) Executive Summary</h2>
            <div className={styles.card}>
              <p>
                MyPeopleDatabase is a privacy-first system for managing the people in your life with
                richer context than a traditional address book: notes, history, reminders, groups,
                and relationship intelligence.
              </p>
              <p>
                A core wedge is MPD ID: a shareable identity page with a QR code that turns real-world
                introductions into saved relationships via a simple loop (scan → view → connect).
                Private groups and selective sharing make this safe for families, teams, and
                communities.
              </p>
              <p>
                The business succeeds by combining a compelling personal value proposition (better
                memory + better relationships) with predictable recurring revenue, low marginal
                costs, and a distribution strategy centered on communities, affiliates, and
                product-led growth.
              </p>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>2) Problem & Market Opportunity</h2>
            <div className={styles.grid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>The problem</h3>
                <ul className={styles.list}>
                  <li>
                    People forget context: who someone is, why they matter, and where they fit.
                  </li>
                  <li>Contacts apps are shallow; CRMs are heavy and built for sales teams.</li>
                  <li>Most tools monetize attention, not user outcomes, creating trust issues.</li>
                </ul>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>The opportunity</h3>
                <ul className={styles.list}>
                  <li>
                    Personal knowledge management is mainstream; relationships are a core dataset.
                  </li>
                  <li>Users want durable tools that respect privacy and data ownership.</li>
                  <li>
                    Recurring revenue is justified when the tool becomes a daily/weekly habit.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>3) Target Customers & Use Cases</h2>
            <div className={styles.card}>
              <ul className={styles.list}>
                <li>
                  Relationship-focused individuals: staying in touch, remembering life events,
                  intentional follow-ups.
                </li>
                <li>
                  Professionals and founders: lightweight relationship intelligence without a full
                  CRM.
                </li>
                <li>
                  Community builders: groups, cohorts, clubs, alumni networks, faith communities.
                </li>
                <li>
                  Caregiving and family history: preserving context, medical/eldercare notes,
                  milestones.
                </li>
              </ul>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>4) Product & Differentiation</h2>
            <div className={styles.grid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Core value</h3>
                <ul className={styles.list}>
                  <li>Richer profiles: notes, events, memories, links, attachments.</li>
                  <li>Groups and tags: organize by context, not just name/phone.</li>
                  <li>Reminders: follow-up nudges and relationship maintenance workflows.</li>
                  <li>Privacy-first: user control and clear data ownership stance.</li>
                </ul>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Why we win</h3>
                <ul className={styles.list}>
                  <li>Positioned between contacts apps (too simple) and CRMs (too complex).</li>
                  <li>Trust and longevity: product is built to be an archive, not a feed.</li>
                  <li>
                    Clear upsell path: advanced features are genuinely valuable, not dark patterns.
                  </li>
                </ul>
              </div>
            </div>

            <div className={styles.grid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>MPD ID (public profile + QR sharing)</h3>
                <p className={styles.paragraph}>
                  MPD ID is a shareable identity page that turns real-world introductions into saved
                  relationships.
                </p>
                <p className={styles.paragraph}>
                  Think of it as a modern replacement for the business card: always up to date,
                  instantly scannable, and designed for private-by-default connection.
                </p>
                <ul className={styles.list}>
                  <li>
                    A user has a public-facing MPD ID link (and QR code) they can share in-person or
                    online.
                  </li>
                  <li>
                    Scanning the QR opens the MPD ID page and provides a clear “connect” action so
                    both people can link to each other.
                  </li>
                  <li>
                    Sharing is selective: users choose what is public, what is private, and what is
                    shared with specific groups.
                  </li>
                </ul>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Secure private groups + controlled sharing</h3>
                <p className={styles.paragraph}>
                  Groups are not just labels; they are privacy boundaries for real communities.
                </p>
                <ul className={styles.list}>
                  <li>Invite-only groups for families, teams, cohorts, neighborhoods, clubs.</li>
                  <li>
                    Permission-based visibility so users can share specific profile fields and notes
                    with a group without making them public.
                  </li>
                  <li>
                    Shared directories for groups that want a trusted “who’s who” without an
                    attention-based social feed.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>5) Business Model (How We Make Money)</h2>
            <div className={styles.card}>
              <p className={styles.paragraph}>
                The goal is a simple model: a free or low-friction entry point and a paid tier that
                funds product improvement, reliability, and support.
              </p>

              <div className={styles.subsection}>
                <h3 className={styles.subTitle}>Primary revenue: subscriptions</h3>
                <ul className={styles.list}>
                  <li>
                    Personal subscription: unlock advanced reminders, unlimited records, and
                    power-user features.
                  </li>
                  <li>
                    Family plan: shared household context with separate private notes and controlled
                    sharing.
                  </li>
                  <li>
                    Community plan: group-based collaboration, shared directories, and admin tools.
                  </li>
                </ul>
              </div>

              <div className={styles.subsection}>
                <h3 className={styles.subTitle}>Secondary revenue</h3>
                <ul className={styles.list}>
                  <li>
                    One-time purchase option for users who dislike subscriptions (good for trust and
                    conversion).
                  </li>
                  <li>
                    Consulting and implementation services for organizations (setup, customization,
                    migration).
                  </li>
                  <li>
                    Add-ons: encrypted storage upgrades, premium integrations, export/backup
                    tooling.
                  </li>
                </ul>
              </div>

              <div className={styles.subsection}>
                <h3 className={styles.subTitle}>What we will NOT do</h3>
                <ul className={styles.list}>
                  <li>Sell personal relationship data.</li>
                  <li>Run an attention-based advertising business.</li>
                  <li>Build features that reduce trust for short-term revenue.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>6) Go-To-Market (How We Grow)</h2>
            <div className={styles.grid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Product-led growth</h3>
                <ul className={styles.list}>
                  <li>Fast onboarding: import contacts + first 10 “enriched profiles” flow.</li>
                  <li>Habit loops: reminders, weekly review, relationship “health” indicators.</li>
                  <li>Sharing moments: tasteful ways to share a profile or group directory.</li>
                  <li>
                    MPD ID viral loop: QR scan → view MPD ID → connect → mutual link saved in both
                    databases.
                  </li>
                </ul>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Affiliate + community channel</h3>
                <ul className={styles.list}>
                  <li>
                    Affiliate program for creators in PKM, productivity, and community building.
                  </li>
                  <li>Partnerships with cohort-based courses and community platforms.</li>
                  <li>
                    Templates and playbooks (networking, caregiving, alumni, client follow-up).
                  </li>
                </ul>
              </div>
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>MPD ID distribution (QR + links)</h3>
              <ul className={styles.list}>
                <li>
                  QR codes are built-in to the product so every user has a natural “share my ID”
                  moment.
                </li>
                <li>
                  The product earns the share: MPD ID is useful even for non-users (viewable page),
                  but conversion happens when someone wants to connect, save, or join a private
                  group.
                </li>
                <li>
                  Groups drive concentration: one community admin can onboard an entire cohort into
                  a private directory with controlled sharing.
                </li>
              </ul>
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>What we measure (growth + retention)</h3>
              <ul className={styles.list}>
                <li>Activation: time to first useful profile + first reminder.</li>
                <li>Sharing: MPD ID views per user, QR scans per user, connect conversion rate.</li>
                <li>Network depth: new links created per week, group invites accepted.</li>
                <li>Retention: weekly active users and reminder completion/engagement.</li>
              </ul>
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Early traction playbook (practical)</h3>
              <ol className={styles.orderedList}>
                <li>Choose 1-2 beachhead segments (e.g., community builders + PKM enthusiasts).</li>
                <li>Ship a crisp “before/after” onboarding demo and landing page narrative.</li>
                <li>Recruit 20-50 power users and iterate weekly on retention signals.</li>
                <li>Turn wins into case studies and short videos affiliates can reuse.</li>
              </ol>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>7) Costs, Unit Economics, and Sustainability</h2>
            <div className={styles.card}>
              <ul className={styles.list}>
                <li>
                  Main costs: hosting, storage, email, support, payment fees, and ongoing
                  development.
                </li>
                <li>
                  Subscription pricing must cover support + infra + product development and still
                  maintain a strong gross margin.
                </li>
                <li>
                  The product should be engineered for low marginal cost per user via efficient data
                  storage, rate-limited AI features, and sensible feature gating.
                </li>
              </ul>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>8) Risks & Mitigations</h2>
            <div className={styles.grid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Trust risk</h3>
                <p className={styles.paragraph}>Relationships are sensitive. Trust is the moat.</p>
                <ul className={styles.list}>
                  <li>Clear privacy posture and “data ownership” messaging.</li>
                  <li>Easy exports, transparent security practices, and strong defaults.</li>
                </ul>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Retention risk</h3>
                <p className={styles.paragraph}>Users churn if the tool doesn’t become a habit.</p>
                <ul className={styles.list}>
                  <li>Build weekly value: reminders, review workflows, and progress indicators.</li>
                  <li>Focus on time-to-value: first useful reminder within 7 days.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>9) Milestones</h2>
            <div className={styles.card}>
              <ol className={styles.orderedList}>
                <li>
                  MVP retention: demonstrate users returning weekly and actively updating profiles.
                </li>
                <li>Pricing validation: prove willingness to pay with subscription conversion.</li>
                <li>
                  Channel validation: affiliates and communities produce consistent qualified
                  traffic.
                </li>
                <li>
                  Operational maturity: reliable backups, support process, and clear incident
                  response.
                </li>
              </ol>
            </div>
          </section>

          <div className={styles.linksSection}>
            <Link to="/vision" className={styles.linkButton}>
              Back to Vision →
            </Link>
            <Link to="/buy" className={styles.linkButton}>
              See Pricing & Purchase Options →
            </Link>
            <Link to="/affiliate-program" className={styles.linkButton}>
              Grow With the Affiliate Program →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
